/**
 * LocationLink Component
 * 
 * Displays location with a clickable link that opens a modal
 * allowing users to choose between Google Maps, Apple Maps, or Waze
 * 
 * Props:
 * - location: The location identifier (name or UUID)
 * - locationName: Optional human-readable location name (for display when location is UUID)
 * - address: Optional address for map directions (falls back to location name)
 * - className: Optional CSS classes for styling
 */

import { useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';

interface LocationLinkProps {
  location: string;
  locationName?: string;
  address?: string;
  className?: string;
}

export function LocationLink({ location, locationName, address, className = '' }: LocationLinkProps) {
  const [showMapModal, setShowMapModal] = useState(false);

  // Use locationName for display if provided, otherwise use location
  const displayName = locationName || location;

  // If no location name, return nothing
  if (!displayName) {
    return null;
  }

  // Use address if provided, otherwise use display name for map search
  const searchQuery = address || displayName;

  /**
   * Open map with the specified app
   */
  const openMap = (app: 'google' | 'apple' | 'waze') => {
    const encodedAddress = encodeURIComponent(searchQuery);
    let url = '';
    
    switch (app) {
      case 'google':
        url = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
        break;
      case 'apple':
        url = `http://maps.apple.com/?q=${encodedAddress}`;
        break;
      case 'waze':
        url = `https://waze.com/ul?q=${encodedAddress}&navigate=yes`;
        break;
    }
    
    window.open(url, '_blank');
    setShowMapModal(false);
  };

  return (
    <>
      <div className={className}>
        <button
          onClick={() => setShowMapModal(true)}
          className="text-primary-600 hover:text-primary-700 underline text-left bg-transparent border-none p-0 cursor-pointer block"
          aria-label="Get directions"
        >
          <div>{displayName}</div>
          {address && (
            <div className="text-gray-600 mt-1">{address}</div>
          )}
        </button>
      </div>

      {/* Map Selection Modal */}
      <Modal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        title="Choose Map App"
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-4">
            Select which map app you'd like to use for directions:
          </p>
          
          <button
            onClick={() => openMap('google')}
            className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900">Google Maps</p>
              <p className="text-sm text-gray-500">Open in Google Maps</p>
            </div>
            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button
            onClick={() => openMap('apple')}
            className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
          >
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900">Apple Maps</p>
              <p className="text-sm text-gray-500">Open in Apple Maps</p>
            </div>
            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button
            onClick={() => openMap('waze')}
            className="w-full flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors touch-manipulation"
          >
            <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-cyan-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-gray-900">Waze</p>
              <p className="text-sm text-gray-500">Open in Waze</p>
            </div>
            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="pt-3 border-t border-gray-200">
            <Button
              variant="secondary"
              onClick={() => setShowMapModal(false)}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
